import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Truck } from "lucide-react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api, errMsg, fmtDate, fmtMoney } from "@/lib/api";
import { useFiles } from "@/context/FilesContext";
import { Input } from "@/components/ui/input";

const STATUS = {
  approved: ["Validée", "bg-emerald-50 text-emerald-700 border-emerald-200"],
  pending_manager: ["Chez le responsable", "bg-amber-50 text-amber-700 border-amber-200"],
  pending_direction: ["Chez la direction", "bg-amber-50 text-amber-700 border-amber-200"],
  rejected: ["Refusée", "bg-red-50 text-red-700 border-red-200"],
};

const Status = ({ s }) => {
  const [label, cls] = STATUS[s] || ["Non soumise", "bg-slate-50 text-slate-600 border-slate-200"];
  return <span className={`text-[11px] font-semibold border rounded-full px-2 py-0.5 whitespace-nowrap ${cls}`}>{label}</span>;
};

const Stat = ({ label, value, tone = "", testId }) => (
  <div className="bg-white border rounded-xl p-4" data-testid={testId}>
    <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
    <div className={`font-display text-2xl font-bold mt-1 ${tone}`}>{value}</div>
  </div>
);

function SupplierList() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  useEffect(() => { api.get("/suppliers").then((r) => setItems(r.data)).catch((e) => setError(errMsg(e))); }, []);
  const shown = (items || []).filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="max-w-6xl mx-auto fade-up" data-testid="suppliers-page">
      <p className="text-xs uppercase tracking-wider font-semibold text-slate-500">Achats</p>
      <div className="flex items-end justify-between gap-4 flex-wrap mt-1 mb-6">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Fournisseurs</h1>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un fournisseur…" className="w-full sm:w-72 bg-white" data-testid="suppliers-search" />
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!items && !error && <Loader2 className="h-5 w-5 animate-spin" />}
      {items && shown.length === 0 && <div className="border border-dashed rounded-xl py-12 text-center text-sm text-slate-500" data-testid="suppliers-empty">Aucune facture fournisseur pour le moment.</div>}
      {shown.length > 0 && (
        <div className="bg-white border rounded-xl overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="suppliers-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-3">Fournisseur</th><th className="px-4 py-3 text-right">Factures</th><th className="px-4 py-3 text-right">Total TTC</th><th className="px-4 py-3 text-right">Non validées</th><th className="px-4 py-3">Prochaine échéance</th><th className="px-4 py-3">Dernière facture</th></tr>
            </thead>
            <tbody className="divide-y">
              {shown.map((s) => (
                <tr key={s.key} onClick={() => navigate(`/fournisseurs/${s.key}`)} className="hover:bg-blue-50/50 cursor-pointer" data-testid={`supplier-row-${s.key}`}>
                  <td className="px-4 py-3 font-semibold"><span className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-[#1E3A8A]" />{s.name}</span></td>
                  <td className="px-4 py-3 text-right">{s.invoice_count}</td>
                  <td className="px-4 py-3 text-right font-semibold tabular-nums">{fmtMoney(s.total_ttc)}</td>
                  <td className="px-4 py-3 text-right">{s.open}{s.overdue > 0 && <span className="ml-1.5 text-[11px] font-semibold text-red-600">({s.overdue} échue{s.overdue > 1 ? "s" : ""})</span>}</td>
                  <td className="px-4 py-3">{s.next_due ? fmtDate(s.next_due) : "—"}</td>
                  <td className="px-4 py-3">{s.last_invoice ? fmtDate(s.last_invoice) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SupplierDetail({ supplierKey }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const { openFile } = useFiles();
  useEffect(() => { api.get(`/suppliers/${supplierKey}`).then((r) => setD(r.data)).catch((e) => setError(errMsg(e))); }, [supplierKey]);
  if (error) return <p className="text-sm text-red-600 max-w-6xl mx-auto">{error}</p>;
  if (!d) return <Loader2 className="h-5 w-5 animate-spin" />;
  return (
    <div className="max-w-6xl mx-auto fade-up space-y-6" data-testid="supplier-detail">
      <div>
        <Link to="/fournisseurs" className="text-sm text-slate-500 hover:text-[#1E3A8A] inline-flex items-center gap-1" data-testid="supplier-back"><ArrowLeft className="h-4 w-4" />Tous les fournisseurs</Link>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-2" data-testid="supplier-name">{d.name}</h1>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Dépensé sur 12 mois" value={fmtMoney(d.total_12m)} testId="supplier-total-12m" />
        <Stat label="Factures" value={d.invoice_count} testId="supplier-invoices-count" />
        <Stat label="Non validées" value={d.open} tone={d.open ? "text-amber-600" : ""} testId="supplier-open" />
        <Stat label="Échues non validées" value={d.overdue} tone={d.overdue ? "text-red-600" : ""} testId="supplier-overdue" />
      </div>
      <div className="bg-white border rounded-xl p-5" data-testid="supplier-monthly-chart">
        <h2 className="font-display font-bold mb-4">Dépenses TTC par mois</h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.monthly} margin={{ left: 8, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval={0} angle={-30} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${Math.round(v)}`)} width={40} />
              <Tooltip formatter={(v) => fmtMoney(v)} labelStyle={{ fontWeight: 600 }} />
              <Bar dataKey="total" name="Total TTC" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border rounded-xl overflow-x-auto">
          <h2 className="font-display font-bold px-5 pt-5 pb-3">Factures</h2>
          <table className="w-full text-sm min-w-[560px]" data-testid="supplier-invoices">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr><th className="px-4 py-2.5">N°</th><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5 text-right">TTC</th><th className="px-4 py-2.5">Échéance</th><th className="px-4 py-2.5">Validation</th></tr>
            </thead>
            <tbody className="divide-y">
              {d.invoices.map((i) => (
                <tr key={i.id} onClick={() => openFile(i.id)} className="hover:bg-blue-50/50 cursor-pointer" data-testid={`supplier-invoice-${i.id}`}>
                  <td className="px-4 py-2.5 font-medium">{i.number || i.name}</td>
                  <td className="px-4 py-2.5">{i.date ? fmtDate(i.date) : "—"}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{i.amount_ttc != null ? fmtMoney(i.amount_ttc) : "—"}</td>
                  <td className="px-4 py-2.5">{i.expiry_date ? fmtDate(i.expiry_date) : "—"}</td>
                  <td className="px-4 py-2.5"><Status s={i.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white border rounded-xl p-5" data-testid="supplier-upcoming">
          <h2 className="font-display font-bold mb-3">Prochaines échéances</h2>
          {d.upcoming.length === 0 ? <p className="text-sm text-slate-500">Aucune échéance à venir.</p> : (
            <ul className="space-y-2">
              {d.upcoming.map((i) => (
                <li key={i.id}>
                  <button onClick={() => openFile(i.id)} className="w-full text-left border rounded-lg px-3 py-2 hover:border-[#1E3A8A] transition-colors">
                    <div className="flex justify-between gap-2 text-sm"><span className="font-semibold">{fmtDate(i.expiry_date)}</span><span className="tabular-nums">{i.amount_ttc != null ? fmtMoney(i.amount_ttc) : ""}</span></div>
                    <div className="text-xs text-slate-500 truncate">{i.number || i.name}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Suppliers() {
  const { supplierKey } = useParams();
  return supplierKey ? <SupplierDetail supplierKey={supplierKey} /> : <SupplierList />;
}
